<template>
  <div class="pokemon-card shadow-lg border-0 overflow-hidden" v-if="pokemon">
    <div class="card-header-gradient p-3 text-center position-relative">
      <div class="shiny-sparkle" v-if="pokemon.isShiny"></div>
      <div class="sprite-shell" :class="{ loaded: spriteLoaded, error: spriteError }">
        <div v-if="!spriteLoaded && !spriteError" class="sprite-skeleton"></div>
        <div v-if="showSpriteSpinner && !spriteError" class="sprite-spinner">
          <i class="fa fa-circle-o-notch fa-spin"></i>
        </div>
        <div v-if="spriteError" class="sprite-fallback">
          <i class="fa fa-picture-o"></i>
          <span>Sprite unavailable</span>
        </div>
        <img
          :src="spriteUrl"
          class="pokemon-sprite img-fluid"
          :class="{ hidden: !spriteLoaded || spriteError }"
          @load="onSpriteLoad"
          @error="onSpriteError" />
      </div>
      <div class="lvl-badge">Lv. {{ pokemon.level }}</div>
    </div>
    
    <div class="card-body bg-dark p-3">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 font-weight-bold text-white">
            <span :class="{'pokemon-name-rainbow': pokemon.isShiny}">{{ displayName }}</span>
            <i v-if="pokemon.gender === 'M'" class="fa fa-mars text-info ml-1 small"></i>
            <i v-if="pokemon.gender === 'F'" class="fa fa-venus text-danger ml-1 small"></i>
          </h5>
          <div v-if="formName || pokemon.isShiny" class="form-subtitle">
            <span v-if="formName">{{ formName }}</span>
            <span v-if="pokemon.isShiny" class="shiny-label"><i class="fa fa-star"></i> Shiny</span>
          </div>
        </div>
        <div class="d-flex flex-column align-items-end">
          <span class="badge badge-primary px-2">{{ natureName }}</span>
          <span class="pokemon-id">#{{ pokemon.dex }}</span>
        </div>
      </div>

      <div class="small text-muted mb-3 d-flex align-items-center">
         <i class="fa fa-heart text-danger mr-1"></i> {{ pokemon.maxHP }} HP 
         <span class="mx-2">|</span>
         <i class="fa fa-smile-o text-warning mr-1"></i> {{ pokemon.happiness }} Happiness
      </div>

      <div class="stats-grid">
        <div class="stat-header">Stat</div>
        <div class="stat-header text-center">IV</div>
        <div class="stat-header text-center">EV</div>

        <template v-for="(name, index) in statNames">
          <div :key="'n'+index" class="stat-name text-muted">{{ name }}</div>
          <div :key="'i'+index" class="stat-value text-center font-weight-bold" :style="getIvStyle(pokemon.ivs[index])">
            {{ pokemon.ivs[index] }}
          </div>
          <div :key="'e'+index" class="stat-value text-center text-secondary">
            {{ pokemon.evs[index] }}
          </div>
        </template>
      </div>

      <div class="mt-3 pt-2 border-top border-secondary-light d-flex justify-content-between">
        <div class="small">
          <span class="text-muted mr-1">Ability:</span>
          <span class="text-info">{{ abilityName }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Natures from '../pokemon/_natures.js'
import Monsters from '../pokemon/_monsters.js'
import Abilities from '../pokemon/_abilities.js'

export default {
  name: 'PokemonCard',
  props: {
    statsString: String,
    name: String
  },
  data() {
    return {
      statNames: ['ATK', 'DEF', 'SPD', 'SATK', 'SDEF', 'HP'],
      spriteLoaded: false,
      spriteError: false,
      showSpriteSpinner: false,
      spriteSpinnerTimer: null
    }
  },
  computed: {
    pokemon() {
      if (!this.statsString) return null;
      const s = this.statsString.split(',');
      if (s.length < 27) return null;

      // Original order in link: 
      // 0:Dex, 1:Lvl, 2:IsShiny, 3:IVHP, 4:IVATK, 5:IVDEF, 6:IVSPD, 7:IVSPATK, 8:IVSPDEF... 
      // 21:EVHP, 22:EVATK, 23:EVDEF, 24:EVSPD, 25:EVSPATK, 26:EVSPDEF
      return {
        dex: s[0],
        level: s[1],
        isShiny: s[2] === '1',
        // UI expects: ATK, DEF, SPD, SATK, SDEF, HP
        ivs: [s[4], s[5], s[6], s[7], s[8], s[3]],
        evs: [s[22], s[23], s[24], s[25], s[26], s[21]],
        maxHP: s[14],
        gender: s[15],
        abilityId: s[16],
        natureId: s[17],
        happiness: s[18],
        form: s[19],
        region: s[20],
        name: this.name
      };
    },
    natureName() {
      return Natures[this.pokemon.natureId] || 'Unknown';
    },
    abilityName() {
        const abilityId = Number(this.pokemon.abilityId);
        if (!Number.isNaN(abilityId) && Abilities[abilityId]) {
            return Abilities[abilityId];
        }
        const monster = Monsters.find(m => m.ID == this.pokemon.dex);
        if (monster && monster.Ability) {
            return monster.Ability[abilityId - 1] || 'Unknown';
        }
        return 'Unknown';
    },
    spriteUrl() {
      const baseUrl = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/'
      const shinyPath = this.pokemon.isShiny ? 'shiny/' : ''
      return `${baseUrl}${shinyPath}${this.pokemon.dex}.png`
    },
    displayName() {
      return this.pokemon.name || this.name || '';
    },
    formName() {
      return this.resolveFormName();
    }
  },
  methods: {
    resolveFormName() {
      const formId = Number(this.pokemon.form);
      if (!formId) return '';
      const mapping = (this.$store && this.$store.getters && this.$store.getters.formMapping) || {};
      const speciesMappings = mapping.mappings || {};
      const monsterId = Number(this.pokemon.dex);
      if (!speciesMappings[monsterId]) {
        return '';
      }
      const forms = speciesMappings[monsterId];
      const match = forms.find(f => Number(f.id) === formId);
      if (!match) return '';
      const duplicates = forms.filter(f => f.name === match.name);
      if (duplicates.length > 1) {
        const idx = duplicates.findIndex(f => Number(f.id) === formId) + 1;
        return `${match.name} (${idx})`;
      }
      return match.name;
    },
    onSpriteLoad() {
      this.spriteLoaded = true;
      this.spriteError = false;
      this.clearSpriteSpinner();
    },
    onSpriteError() {
      this.spriteError = true;
      this.spriteLoaded = false;
      this.clearSpriteSpinner();
    },
    clearSpriteSpinner() {
      if (this.spriteSpinnerTimer) {
        clearTimeout(this.spriteSpinnerTimer);
        this.spriteSpinnerTimer = null;
      }
      this.showSpriteSpinner = false;
    },
    queueSpriteSpinner() {
      this.clearSpriteSpinner();
      this.spriteSpinnerTimer = setTimeout(() => {
        if (!this.spriteLoaded && !this.spriteError) {
          this.showSpriteSpinner = true;
        }
      }, 450);
    },
    getIvStyle(val) {
      val = Number(val);
      if (val > 29) return { color: '#4dbd74' };
      if (val > 19) return { color: '#a8965c' };
      if (val < 10) return { color: '#f86c6b' };
      return { color: '#f0f3f5' };
    }
  },
  watch: {
    spriteUrl() {
      this.spriteLoaded = false;
      this.spriteError = false;
      this.queueSpriteSpinner();
    }
  },
  beforeDestroy() {
    this.clearSpriteSpinner();
  }
}
</script>

<style scoped lang="scss">
.pokemon-card {
  width: 280px;
  background: #23282c;
  border-radius: 8px;
  overflow: hidden;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.card-header-gradient {
  background: linear-gradient(135deg, #2c3e50 0%, #000000 100%);
  min-height: 140px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pokemon-sprite {
  height: 120px;
  z-index: 2;
  filter: drop-shadow(0 0 10px rgba(0,0,0,0.5));
}

.pokemon-sprite.hidden {
  display: none;
}

.sprite-shell {
  position: relative;
  height: 120px;
  width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sprite-skeleton {
  height: 100px;
  width: 100px;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0.18), rgba(255,255,255,0.06));
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.06);
}

.sprite-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #73818f;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.sprite-fallback i {
  font-size: 26px;
}

.sprite-spinner {
  position: absolute;
  bottom: -8px;
  right: -8px;
  background: rgba(16, 20, 24, 0.75);
  color: #9fd6ff;
  border-radius: 999px;
  padding: 6px;
  box-shadow: 0 6px 16px rgba(0,0,0,0.4);
  font-size: 14px;
}

@keyframes shimmer {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

.lvl-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.6);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  color: #fff;
  font-weight: bold;
  border: 1px solid rgba(255,255,255,0.1);
}

.shiny-sparkle {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-image: url('https://www.transparenttextures.com/patterns/stardust.png');
  opacity: 0.3;
  pointer-events: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 4px;
  margin-top: 10px;
}

.stat-header {
  font-size: 10px;
  text-transform: uppercase;
  color: #73818f;
  font-weight: bold;
  padding-bottom: 4px;
}

.stat-name { font-size: 11px; font-weight: 600; }
.stat-value { font-size: 12px; }

.border-top { border-top: 1px solid #2f353a !important; }
.secondary-light { border-color: #3e444b !important; }

.small { font-size: 12px; }
.tiny-label { font-size: 10px; }

.form-subtitle {
  font-size: 12px;
  color: #ffb166;
  font-weight: 600;
  letter-spacing: 0.3px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.shiny-label {
  color: #ffd24a;
  font-weight: 700;
}

.pokemon-name-rainbow {
  display: inline-block;
  background-image: linear-gradient(90deg, #ff4d4d, #ffb84d, #fff24d, #7dff4d, #4dd2ff, #7f6dff, #ff4df3, #ff4d4d);
  background-size: 200% 100%;
  animation: pokemon-rainbow 3s linear infinite;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
}

.pokemon-id {
  font-size: 11px;
  color: #7f8b96;
  margin-top: 4px;
}

@keyframes pokemon-rainbow {
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
}

@media (prefers-reduced-motion: reduce) {
  .pokemon-name-rainbow {
    animation: none;
    background-image: none;
    color: #e4e7ea;
  }
}
</style>
